import { useSwaggerlessContext } from 'Context/SwaggerlessContext';
import React, { useEffect } from 'react';
import { NotificationContainer } from 'react-notifications';
import AppContext from 'Context/AppContext';
import useUserContext from 'Context/UserContext';
import { Elements, StripeProvider } from 'react-stripe-elements';
import Router, { Route, Switch } from 'Components/UI/FacetRouter/FacetRouter';
import { dashboardUri, paymentUri } from 'Consts/clientUris';
import useAppState from 'Hooks/useAppState';
import { Redirect } from 'react-router';
import './Assets/EuclidSquare.css';

import {
  advisorPath,
  changePasswordPath,
  changePasswordPathExperimental,
  clientDashboardPath2,
  clientPath,
  loginExperimentalPath,
  loginPath,
  maintenancePath,
  roleSelectionPath,
  twoFactorPath,
  userAuthSetupPath,
} from 'Consts/Strings';
import AdvisorApp from 'Components/AdvisorApp';
import ClientApp from 'Components/ClientApp';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { Login as _Login, LoginExperimental as _LoginExperimental } from 'Views/Login';
import PaymentModal from 'Components/Modals/PaymentModal';
import 'App.css';
import { adminRoot } from 'Consts/advisorUris';
import { stripeNonSecretKey } from 'Consts/keys';
import _Maintenance from 'Components/Maintenance';
import ModalRouter from 'Components/Modals/ModalRouter';
import history from 'State/history';
import { CLIENT_ROLE } from 'Consts/Role';
import { PasswordReset as _PasswordReset } from 'Views/PasswordReset';
import * as SentryReact from '@sentry/react';
import _UserAuthSetup from 'Views/UserAuthSetup';
import { PasswordResetExperimental as _PasswordResetExperimental } from 'Views/PasswordReset/PasswordResetExperimental';
import { ModalProvider } from 'Views/Page/Contexts/ModalContext';
import {
  RoleSelectionBaseLayout,
} from 'Views/Authentication/Layout/RoleSelectionLayout/RoleSelectionBaseLayout';
import Sentry from './Integrations/Sentry';

// Wrap a bunch of components in modal provider, since they've had it around them for a while now and at least some of them now depend on it (tests failed without this)
const withModalProvider = Wrapped => props => <ModalProvider><Wrapped {...props} /></ModalProvider>;
const UserAuthSetup = withModalProvider(_UserAuthSetup);
const Login = withModalProvider(_Login);
const LoginExperimental = withModalProvider(_LoginExperimental);
const Maintenance = withModalProvider(_Maintenance);
const PasswordReset = withModalProvider(_PasswordReset);
const PasswordResetExperimental = withModalProvider(_PasswordResetExperimental);

const App = () => {
  const { getNotifications, markNotificationsRead, updateNotification } = useSwaggerlessContext();
  const { user } = useUserContext();
  const { householdId } = user || 0;
  const { loadHousehold } = useSimpleHousehold();

  const { userTypes } = user || {};

  const appState = useAppState({
    getNotifications,
    updateNotification,
    markNotificationsRead,
  });

  if (user) {
    Sentry.setUser(user);
  }

  useEffect(() => {
    if (householdId > 0) {
      loadHousehold(householdId);
    }
  }, [householdId]);

  let jsx = (
    <Router>
        <>
          <AppContext.Provider value={appState}>
            <ModalRouter />
          </AppContext.Provider>
          <NotificationContainer />
          <Switch>
            <Route path={maintenancePath} component={Maintenance} />
            <Route path={changePasswordPath} component={PasswordReset} />
            <Route path={changePasswordPathExperimental} component={PasswordResetExperimental} />
            <Route
              path={paymentUri}
              render={(routerProps) => {
                if (user && user.householdId && user.householdId > 0) {
                  return (
                    <ModalProvider>
                      <PaymentModal
                        {...routerProps}
                        showCloseIcon={false}
                      />
                    </ModalProvider>
                  );
                }
                return null;
              }}
            />
            <Route
              path={advisorPath}
              render={(routeProps) => {
                if (userTypes && userTypes.includes('agent')) {
                  return <AdvisorApp {...routeProps} />;
                }
                if (userTypes && userTypes.includes(CLIENT_ROLE)) {
                  return <Redirect to={clientDashboardPath2} />;
                }
                return null;
              }}
            />
            {/* Resetting your password requires a dispatch to redux to update the user and api key in the redux store.
                  Before redux subscribers receive these updates, this router will render and fall through to the login page.
                  To avoid that, I'm returning null until `user` has been populated in redux. */}
            <Route
              path={clientPath}
              render={(routeProps) => {
                if (userTypes && userTypes.includes(CLIENT_ROLE)) {
                  return <ClientApp {...routeProps} />; // This actually renders the modal provider in a special way
                }
                if (userTypes && userTypes.includes('agent')) {
                  return <Redirect to={adminRoot} />;
                }
                return null;
              }}
            />
            {/* <Route path={fileDownloadUri} render={window.open(fileDownloadUri)} /> */}
            <Route path={userAuthSetupPath} component={UserAuthSetup} />
            <Route path={loginPath} component={Login} />
            <Route path={loginExperimentalPath} component={LoginExperimental} />
            <Route path={roleSelectionPath} component={RoleSelectionBaseLayout} />
            <Route path={twoFactorPath} component={Login} />
            <Route
              render={(routeProps) => {
                if (userTypes && userTypes.includes('client')) {
                  history.push(dashboardUri);
                  return null;
                }
                if (userTypes && userTypes.includes('agent')) {
                  history.push(adminRoot);
                  return null;
                }
                return <ModalProvider><Login {...routeProps} /></ModalProvider>;
              }}
            />
          </Switch>
        </>
    </Router>
  );

  if (stripeNonSecretKey && window.Stripe) {
    jsx = (
      <StripeProvider apiKey={stripeNonSecretKey}>
        <Elements>{jsx}</Elements>
      </StripeProvider>
    );
  }

  return jsx;
};

App.keyWatcher = null;

App.propTypes = {
};

const AppContainer = SentryReact.withProfiler(App);

export default AppContainer;
